import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { useResolvedAppTestProduct } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useResolvedAppTestProduct'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { requireDepositBeforeKYC } from '@utils/AccountAccess/accountKYCStatuses'
import {
  isEconomicProfileTestNeeded,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths, getProductAppTestRiskAcceptancePath } from '@utils/path'

import { AccountDetailedDto, isVerificationExpedited } from '@model/AccountDetailedDto'
import { CreateClientTest } from '@model/CreateClientTest'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryOptionType } from '@model/DocumentCategoryOptionType'
import { DocumentPropertyType } from '@model/DocumentPropertyType'
import { TestFormValues } from '@model/Test'
import { TickmillProductType } from '@model/TickmillProductType'

import { useLandingPage } from '@hooks/useLandingPage'
import { scrollToTop } from '@hooks/useScrollToTop'

import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'
import { TestForm, TestFormRef } from '@shared/templates/Test/TestForm'
import { TestTemplate } from '@shared/templates/TestTemplate'

const useEconomicProfileTestMutate = (clientId?: string, productId?: TickmillProductType) => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const locale = useSessionLanguage()
  const [isLoading, setLoading] = useState(false)
  const { refreshAccount } = useAccountWriteContext()

  const alreadyUploadedModal = useModal(AlreadyUploadedModal)

  const mutate = async (values: TestFormValues): Promise<AccountDetailedDto | undefined> => {
    try {
      setLoading(true)

      await apiClient.createClientTests(dataToSubmit(values))
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      const accountApTestAnswers = await apiClient.getAccountAppTestAnswers(locale, productId)

      const files = Object.entries(values.documents || {})
        .map(([questionId, filesData]) => filesData.map((x) => ({ ...x, questionId })).flat())
        .flat()
        .filter((x) => !!x.fileName)

      if (files.length > 0) {
        await apiClient.uploadDocuments({
          documents: files.map((file) => ({
            categoryId: DocumentCategoryType.AppropriatenessTest,
            typeId: DocumentCategoryOptionType.TradingStatement,
            file: file.base64Content,
            filename: file.fileName,
            properties: {
              [DocumentPropertyType.ClientTestId]: accountApTestAnswers.id,
              [DocumentPropertyType.TestQuestionId]: file.questionId,
            },
          })),
        })
      }

      const refreshedAccount = await refreshAccount(locale)
      scrollToTop()
      return refreshedAccount
    } catch (error: unknown) {
      alreadyUploadedModal.open({ message: (error as any).response?.response?.data?.code })
      console.error('Error submitting economic profile test', error)
      return undefined
    } finally {
      setLoading(false)
    }
  }

  const dataToSubmit = (values: TestFormValues): CreateClientTest => {
    const selectedAnswers = Object.values(values.selectedAnswers).flatMap((value) => value)
    const freeAnswers = Object.entries(values.freeAnswers).map(([key, value]) => {
      return { testQuestionId: key, answer: value }
    })

    return {
      category: values?.category || '',
      clientId: clientId || '',
      testId: values?.testId || '',
      selectedAnswers,
      freeAnswers,
    }
  }

  return {
    mutate,
    isLoading,
  }
}

export const ProductEconomicProfilePage: React.FC = () => {
  const apiClient = useApiClient(ClientApiClient)
  const resolvedProductId = useResolvedAppTestProduct()

  const { account } = useAccountReadContext()
  const { getLandingPage } = useLandingPage()

  const locale = useSessionLanguage()
  const navigate = useNavigate()
  const contentRef = useRef<HTMLDivElement>(null)
  const childRef = useRef<TestFormRef>(null)
  const isSubmittingRef = useRef(false)

  const [formValues, setFormValues] = useState<TestFormValues>()
  const [step, setStep] = useState('')

  const mutation = useEconomicProfileTestMutate(account?.id, resolvedProductId)
  const shouldLoadEconomicProfile =
    !account || isEconomicProfileTestNeeded(account, resolvedProductId)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.productRegistration.economicProfileTest(locale, resolvedProductId),
    queryFn: () => apiClient.getEconomicProfileTest(locale, resolvedProductId),
    enabled: shouldLoadEconomicProfile,
  })

  useEffect(() => {
    if (!account) return
    if (showApptestRiskScreen(account, resolvedProductId)) {
      navigate(getProductAppTestRiskAcceptancePath(resolvedProductId), { replace: true })
      return
    }
    if (!isEconomicProfileTestNeeded(account, resolvedProductId)) {
      if (isSubmittingRef.current) return
      navigate(getLandingPage(resolvedProductId), { replace: true })
    }
  }, [account, getLandingPage, navigate, resolvedProductId])

  useEffect(() => {
    const [section] = data?.sections || []
    setStep(section?.code || '')
  }, [account, data?.sections])

  const navAfterSubmit = (submittedAccount: AccountDetailedDto) => {
    if (requireDepositBeforeKYC(submittedAccount) || isVerificationExpedited(submittedAccount)) {
      navigate(CommonPaths.AccountLoading)
      return
    }
    navigate('./success')
    return
  }

  const handleSubmit = async (values: TestFormValues) => {
    isSubmittingRef.current = true
    const submittedAccount = await mutation.mutate(values)
    if (submittedAccount) {
      navAfterSubmit(submittedAccount)
      return
    }
    isSubmittingRef.current = false
  }

  const handleNextStep = (values: TestFormValues) => {
    const sections = data?.sections || []
    const idx = sections.findIndex((x) => x.code === step)
    const section = sections?.[idx + 1]
    if (section?.code) {
      setStep(section.code)
      setFormValues(values)
      scrollToTop()
    }
  }

  const handlePrevStep = (values: TestFormValues) => {
    const sections = data?.sections || []
    const idx = sections.findIndex((x) => x.code === step)
    const section = sections?.[idx - 1]

    if (section?.code) {
      setStep(section.code)
      setFormValues(values)
      scrollToTop()
    }
  }

  const isFirstSection = useMemo(() => {
    const firstSection = data?.sections[0]
    return firstSection?.code === step
  }, [data, step])

  const handleChangeStepPrev = (values: TestFormValues) => {
    handlePrevStep(values)
  }

  const handleChangeStepNext = (values: TestFormValues) => {
    handleNextStep(values)
  }

  const title = data?.sections.find((section) => section.code === step)?.name

  return (
    <TestTemplate
      maxWidth={456}
      isLoading={isLoading}
      contentRef={contentRef}
      isFirstSection={isFirstSection}
      handleChangeStepPrev={() => childRef.current?.goPrevStep()}
      noExit
      title={title}
    >
      {data && (
        <TestForm
          ref={childRef}
          noTitle
          data={data}
          step={step}
          contentRef={contentRef}
          centered
          values={formValues}
          goPrevStep={handleChangeStepPrev}
          goNextStep={handleChangeStepNext}
          onSubmit={handleSubmit}
        />
      )}
    </TestTemplate>
  )
}
